



































import { computed, defineComponent, PropType, ref } from "@vue/composition-api";
import { Participant } from "@scrile/api-provider/dist/api/ParticipantProvider";
import { PersonalInformation } from "@scrile/api-provider/dist/api/PersonalInformationProvider";
import { UserPublicInformation } from "@scrile/api-provider/dist/api/UserPublicDataProvider";
import { UserVerification } from "@scrile/api-provider/dist/api/UserVerificationProvider";
import { UserPublicData } from "@scrile/api-provider/dist/projects/scrileConnect/UserPublicDataProvider";
import useAuthUser from "@/hooks/useAuthUser";
import useUserVerification from "@/hooks/useUserVerification";
import providers from "@/lib/api";
import { $t } from "@/lib/i18nHelpers";
import { format, formatDateOnly } from "@/lib/time";
import BaseSpinner from "@/components/BaseSpinner";
import ConsentDataDescription from "./ConsentDataDescription.vue";

export default defineComponent({
  name: "ConsentFormVerifiedView",
  components: { ConsentDataDescription, BaseSpinner },
  props: {
    participant: { type: Object as PropType<Participant>, required: true },
    owner: { type: Object as PropType<UserPublicData>, default: null },
  },
  setup(props) {
    const { authUser } = useAuthUser();
    const { search } = useUserVerification();
    const loading = ref(true);
    const personalInformation = ref<PersonalInformation | null>(null);
    const publicInformation = ref<UserPublicInformation | null>(null);
    const userVerification = ref<UserVerification | null>(null);

    const fullName = computed(() => {
      if (!personalInformation.value?.firstName && !personalInformation.value?.lastName) return null;
      return personalInformation.value.firstName + " " + personalInformation.value.lastName;
    });

    const info = computed(() => {
      if (!personalInformation.value || !userVerification.value || !publicInformation.value) return [];
      return [
        {
          name: $t("Shooting location"),
          value: props.participant.shootingLocation || "-",
        },
        {
          name: $t("Shooting date(s)"),
          value: props.participant.shootingDate || "-",
        },
        {
          name: $t("Legal first name"),
          value: personalInformation.value.firstName,
        },
        {
          name: $t("Legal middle name"),
          value: personalInformation.value.middleName ?? "-",
          class: personalInformation.value.middleName ? undefined : "gray-text",
        },
        {
          name: $t("Legal last name"),
          value: personalInformation.value.lastName,
        },
        {
          name: $t("Date of birth"),
          value: publicInformation.value.dateOfBirth
            ? formatDateOnly(publicInformation.value.dateOfBirth, "MM/dd/yyyy")
            : "-",
        },

        {
          name: $t("Identity verification date"),
          value: userVerification.value.approveTime
            ? format(new Date(userVerification.value.approveTime), "MM/dd/yyyy")
            : "-",
        },
      ];
    });

    const getUserVerification = async () => {
      if (!authUser.value) return;
      const response = await search({ userIds: [authUser.value.id] });
      userVerification.value = response.result[0];
    };

    const getPersonalInfo = async () => {
      if (!authUser.value) return;
      personalInformation.value = await providers.PersonalInformationProvider.find({ id: authUser.value.id });
    };

    const getPublicInfo = async () => {
      if (!authUser.value) return;
      publicInformation.value = await providers.UserPublicDataProvider.userPublicInformationFind(authUser.value.id);
    };

    const getData = async () => {
      try {
        await Promise.all([getPersonalInfo(), getPublicInfo(), getUserVerification()]);
      } finally {
        loading.value = false;
      }
    };

    getData();

    return {
      fullName,
      info,
      loading,
      personalInformation,
      publicInformation,
    };
  },
});
